import React from "react";
import {
  Box,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import ActionButton from "@quest_editor/components/Buttons/ActionButton";
import BackButton from "@quest_editor/components/Buttons/BackButton";
import { useTranslation } from "react-i18next";
import { Manufacturer, manufacturers } from "@quest_editor/mocks";
import { nanoid } from "nanoid";
import debounce from "lodash/debounce";

const searchManufacturers = (input: string): Manufacturer[] => {
  if (!input) return [];

  return manufacturers.filter((m) => m.name.includes(input));
};

interface SearchDialogProps {
  open: boolean;
  onClose: () => void;
  onAdd: (selected: Manufacturer[]) => void;
  inputValue: string;
  existingRows: Manufacturer[];
}

const ManufacturerSearchDialog = ({
  open,
  onClose,
  onAdd,
  inputValue,
  existingRows,
}: SearchDialogProps) => {
  const { t } = useTranslation("questEditor");
  const [input, setInput] = React.useState(inputValue);
  const [results, setResults] = React.useState<Manufacturer[]>([]);
  const [selectedItems, setSelectedItems] = React.useState<Manufacturer[]>([]);

  const performSearch = (word: string) => {
    const searchResults = searchManufacturers(word);
    setResults(searchResults);
  };

  const debouncedSearch = React.useMemo(
    () => debounce((word: string) => performSearch(word), 300),
    [],
  );

  React.useEffect(() => {
    if (open) {
      setInput(inputValue);
      setSelectedItems([]);
      performSearch(inputValue);
    }
  }, [open, inputValue]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInput(val);
    debouncedSearch(val);
  };

  const isExisting = (item: Manufacturer) =>
    existingRows.some((e) => e.name === item.name);

  const handleToggle = (item: Manufacturer) => {
    if (isExisting(item)) return;
    const isSelected = selectedItems.some(
      (i: Manufacturer) => i.id === item.id,
    );
    if (isSelected) {
      setSelectedItems((prev: Manufacturer[]) =>
        prev.filter((i) => i.id !== item.id),
      );
    } else {
      setSelectedItems((prev: Manufacturer[]) => [...prev, item]);
    }
  };

  const handleConfirm = () => {
    const itemsToAdd = selectedItems.map((item: Manufacturer) => ({
      ...item,
      id: nanoid(),
    }));
    onAdd(itemsToAdd);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          minWidth: 400,
          maxWidth: "90vw",
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: "bold", pb: 1 }}>
        {t("common_field.manufacturer.dialog.title")}
      </DialogTitle>
      <DialogContent dividers>
        <TextField
          fullWidth
          size="small"
          label={t("common_field.manufacturer.dialog.label")}
          value={input}
          onChange={handleInputChange}
          autoFocus
          sx={{ mb: 2, mt: 1 }}
        />

        <Box sx={{ minHeight: 250 }}>
          <List dense>
            {results.length > 0 ? (
              results.map((item) => (
                <ListItem key={item.id} disablePadding divider>
                  <ListItemButton
                    onClick={() => handleToggle(item)}
                    sx={{
                      whiteSpace: "nowrap",
                      px: 2,
                    }}
                  >
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={
                          isExisting(item) ||
                          selectedItems.some((i) => i.id === item.id)
                        }
                        disabled={isExisting(item)}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText primary={item.name} />
                  </ListItemButton>
                </ListItem>
              ))
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                align="center"
                sx={{ mt: 5 }}
              >
                {t("common_field.manufacturer.dialog.nodata", {
                  name: input,
                })}
              </Typography>
            )}
          </List>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <BackButton onClick={onClose}>
          {t("common_field.manufacturer.dialog.button.cancel")}
        </BackButton>
        <ActionButton
          onClick={handleConfirm}
          disabled={selectedItems.length === 0}
        >
          {t("common_field.manufacturer.dialog.button.add", {
            count: selectedItems.length,
          })}
        </ActionButton>
      </DialogActions>
    </Dialog>
  );
};

export default ManufacturerSearchDialog;
